export interface Invoice {
  id: string;
  invoiceNumber: string;
  issuerId: string;
  issuerName: string;
  issuerTaxId?: string;
  issuerAddress?: string;
  clientId: string;
  clientName: string;
  clientTaxId?: string;
  clientVatId?: string;
  clientCountry?: string;
  clientCity?: string;
  clientAddress?: string;
  issueDate: string;
  dueDate?: string;
  saleDate?: string;
  placeOfIssue: string;
  invoiceType?: InvoiceType;
  exchangeRate?: number;
  exchangeRateTable?: string;
  tableNumber?: string;
  exchangeRateDate?: string;
  reverseCharge?: boolean;
  bankAccountId?: string;
  bankAccountName?: string;
  bankAccountBankName?: string;
  bankAccountNumber?: string;
  paymentMethod?: PaymentMethod;
  status: InvoiceStatus;
  currency: Currency;
  totalAmount: number;
  taxAmount: number;
  taxAmountInPln: number; // НДС в польских злотых для валютных инвойсов
  notes?: string;
  internalComment?: string; // Внутренний комментарий, не отображается в фактуре
  pdfLanguage?: string; // Язык для генерации PDF
  wfirmaId?: number; // ID фактуры в системе wFirma после синхронизации
  clientWfirmaId?: number; // ID контрагента в системе wFirma
  items: InvoiceItem[];
  client?: {
    id: string;
    name: string;
    wfirmaId?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate?: TaxRate;
  unitOfMeasure?: UnitOfMeasure;
  totalPrice: number;
  createdAt: string;
  updatedAt: string;
}

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED'
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  CNY = 'CNY',
  GBP = 'GBP',
  CAD = 'CAD',
  PLN = 'PLN'
}

export enum InvoiceType {
  STANDARD = 'STANDARD',
  PROFORMA = 'PROFORMA',
  STANDARD_NO_VAT = 'STANDARD_NO_VAT',
  PROFORMA_NO_VAT = 'PROFORMA_NO_VAT'
}

export enum PaymentMethod {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CASH = 'CASH'
}

export enum UnitOfMeasure {
  PIECE = 'PIECE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  KG = 'KG',
  TON = 'TON',
  METER = 'METER',
  SQUARE_METER = 'SQUARE_METER',
  CUBIC_METER = 'CUBIC_METER',
  LITER = 'LITER',
  SET = 'SET'
}

export enum TaxRate {
  VAT_23 = 'VAT_23',
  VAT_8 = 'VAT_8', 
  VAT_5 = 'VAT_5',
  VAT_0 = 'VAT_0',
  NP = 'NP',
  NA = 'NA',
  ZW = 'ZW'
}

export interface CreateInvoiceRequest {
  invoiceNumber: string;
  clientId: string;
  issueDate: string;
  dueDate?: string;
  saleDate?: string;
  placeOfIssue: string;
  invoiceType?: InvoiceType;
  exchangeRate?: number;
  exchangeRateTable?: string;
  tableNumber?: string;
  exchangeRateDate?: string;
  reverseCharge?: boolean;
  bankAccountId?: string;
  paymentMethod?: PaymentMethod;
  status: InvoiceStatus;
  currency: Currency;
  notes?: string;
  internalComment?: string; // Внутренний комментарий, не отображается в фактуре
  pdfLanguage?: string; // Language enum as string
  items: CreateInvoiceItemRequest[];
}

export interface CreateInvoiceItemRequest {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate?: TaxRate;
  unitOfMeasure?: UnitOfMeasure;
}

export interface UpdateInvoiceRequest extends CreateInvoiceRequest {}

export interface ChangeStatusRequest {
  status: InvoiceStatus;
  forceResync?: boolean;
}

export interface GenerateNumberResponse {
  invoiceNumber: string;
}

// Вспомогательные типы для UI
export interface InvoiceFormData {
  invoiceNumber: string;
  clientId: string;
  issueDate: string;
  dueDate: string;
  saleDate: string;
  placeOfIssue: string;
  invoiceType: InvoiceType;
  exchangeRate: number;
  exchangeRateTable: string;
  tableNumber: string;
  exchangeRateDate: string;
  reverseCharge: boolean;
  bankAccountId: string;
  paymentMethod: PaymentMethod;
  status: InvoiceStatus;
  currency: Currency;
  notes: string;
  internalComment: string;
  items: InvoiceItemFormData[];
}

export interface InvoiceItemFormData {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: TaxRate;
  unitOfMeasure: UnitOfMeasure;
}

// Статусы с русскими названиями
export const InvoiceStatusLabels: Record<InvoiceStatus, string> = {
  [InvoiceStatus.DRAFT]: 'Черновик',
  [InvoiceStatus.SENT]: 'Отправлен',
  [InvoiceStatus.PARTIALLY_PAID]: 'Частично оплачен',
  [InvoiceStatus.PAID]: 'Оплачен',
  [InvoiceStatus.OVERDUE]: 'Просрочен',
  [InvoiceStatus.CANCELLED]: 'Отменен'
};

// Валюты с символами
export const CurrencySymbols: Record<Currency, string> = {
  [Currency.USD]: '$',
  [Currency.EUR]: '€',
  [Currency.CNY]: '¥',
  [Currency.GBP]: '£',
  [Currency.CAD]: 'C$',
  [Currency.PLN]: 'zł'
};

// Типы фактур с русскими названиями
export const InvoiceTypeLabels: Record<InvoiceType, string> = {
  [InvoiceType.STANDARD]: 'Обычный инвойс',
  [InvoiceType.PROFORMA]: 'Перформа инвойс',
  [InvoiceType.STANDARD_NO_VAT]: 'Фактура без VAT',
  [InvoiceType.PROFORMA_NO_VAT]: 'Проформа без VAT'
};

// Способы оплаты с названиями
export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.BANK_TRANSFER]: 'Банковский перевод',
  [PaymentMethod.CASH]: 'Наличные'
};

// Единицы измерения с символами
export const UnitOfMeasureSymbols: Record<UnitOfMeasure, string> = {
  [UnitOfMeasure.PIECE]: 'шт.',
  [UnitOfMeasure.HOUR]: 'час',
  [UnitOfMeasure.DAY]: 'день',
  [UnitOfMeasure.WEEK]: 'неделя',
  [UnitOfMeasure.MONTH]: 'месяц',
  [UnitOfMeasure.KG]: 'кг',
  [UnitOfMeasure.TON]: 'т',
  [UnitOfMeasure.METER]: 'м',
  [UnitOfMeasure.SQUARE_METER]: 'м²',
  [UnitOfMeasure.CUBIC_METER]: 'м³',
  [UnitOfMeasure.LITER]: 'л',
  [UnitOfMeasure.SET]: 'компл.'
};

// Налоговые ставки с названиями
export const TaxRateLabels: Record<TaxRate, string> = {
  [TaxRate.VAT_23]: '23%',
  [TaxRate.VAT_8]: '8%',
  [TaxRate.VAT_5]: '5%',
  [TaxRate.VAT_0]: '0%',
  [TaxRate.NP]: 'NP',
  [TaxRate.NA]: 'NA',
  [TaxRate.ZW]: 'ZW'
};

// Налоговые ставки с числовыми значениями для расчетов
export const TaxRateValues: Record<TaxRate, number> = {
  [TaxRate.VAT_23]: 0.23,
  [TaxRate.VAT_8]: 0.08,
  [TaxRate.VAT_5]: 0.05,
  [TaxRate.VAT_0]: 0.00,
  [TaxRate.NP]: 0.00, // Не подлежит налогообложению
  [TaxRate.NA]: 0.00, // Не применимо
  [TaxRate.ZW]: 0.00  // Освобождение от НДС
};
